import React from 'react';
import { isBrowser } from 'react-device-detect';
import FloatingButtons from './floating/FloatingButtons';
import SelectableCaixa from './selectableCaixa';
import { setPerfil } from './controller/Casteller';
import { ProfilePic } from './watchers/UserList';
import moment from 'moment';
import { applyTimeZone, getSubdomain } from '../../utils/utils';
import CopilotGhostText from './copilot/CopilotGhostText';
import { getCastellerShortName } from '../../utils/castellerName';
import { hasAlturaMansFeature } from '../../utils/colla-features';
import { TemporaryPersonClock, temporaryPersonLabel } from './controller/TemporaryPersonBadge';

const COLLA = getSubdomain();

const parseTransformation = t => {
    let [a, b, c, d, x, y] = t;
    return `matrix(${a},${b},${c},${d},${x},${y})`;
};

const descaleFontSize = (fontSize, mat) => {
    let [a, b, c, d, e, f] = mat;
    const scale = Math.sqrt(Math.abs(a*d - b*c))
    return `${fontSize / scale}px`
};

const fixOrientation = (boxTransf, textTransf) => {
    const [a, b, c, d, e, f] = boxTransf;

    // Negative determinant means the box is flipped
    if (a*d - b*c < 0) {
        const [ta, tb, tc, td, te, tf] = textTransf;

        return [
            -ta, tb, -tc, td, te, tf
        ]
    }
    return textTransf;
}

const parseProps = props => {
    let parsedProps = {};
    
    parsedProps.transform = props.transform && parseTransformation(props.transform);
    parsedProps.width = props.width && `${props.width}px`;
    parsedProps.height = props.height && `${props.height}px`;
    parsedProps.backgroundColor = props.fill;
    parsedProps.position = props.relative ? 'relative' : 'absolute'

    return parsedProps;
};

const hasTambor = info =>
    !!parseInt(info?.music) && (parseInt(info?.casteller) !== 1 || COLLA === 'ganapies');

const getName = (info, props) =>
    (!!parseInt(info?.canalla) ? '👶 ' : '') +
    (hasTambor(info) ? '🥁 ' : '') +
    (!!parseInt(info?.novell) ? '🐥 ' : '') +
    (!!parseInt(info?.lesionat) ? '🏥 ' : '') +
    (props.isEscaleta ? '🪜 ' : '') +
    getCastellerShortName(info)

const getFontSize = (info, boxProps, fontSize) => {
    if (!boxProps.transform) return `${fontSize}px`;
    
    const boxTransform = boxProps.transform;
    const [a, b, c, d, e, f] = boxTransform;
    const scale = Math.sqrt(Math.abs(a*d - b*c));
    
    const boxHeight = boxProps.height;
    const boxWidth = boxProps.width;
    
    // Count emojis in the name
    const hasEmojis = info && (
        parseInt(info?.canalla) ||
        hasTambor(info) ||
        parseInt(info?.novell) ||
        parseInt(info?.lesionat)
    );
    
    // Calculate font size based on box dimensions and scale
    const scaledSize = Math.min(boxHeight, boxWidth) / scale;
    const calculatedFontSize = Math.round(14 * scaledSize / 50);
    
    // Reduce font size if there are emojis to fit everything
    return `${hasEmojis ? calculatedFontSize - 2 : calculatedFontSize}px`;
};

function Caixa(props) {
    const { fullAssistencia, castellBeingImported, cordo, showUntil, boxProps, textProps, readonly, perfil, fontSize, rotationVal, pestanya, targetAssistencia, targetEvent } = props;
    const [_boxStyle, _textStyle] = [boxProps, textProps].map(p => parseProps(p));

    const boxStyle = {
        ..._boxStyle,
        backgroundColor: (['sarri'].includes(props.colla) && cordo === showUntil) || props.disabled ? 'rgba(255,225,225,1)' : _boxStyle.backgroundColor,
        visibility: cordo && cordo > showUntil + 1 ? 'hidden' : 'visible',
        outlineStyle: cordo && cordo === showUntil + 1 ? 'dashed' : 'none',
        opacity: props.caixaSelected !== props.id && cordo && cordo === showUntil + 1 ? 0.25 : props.opacity,
        borderStyle: castellBeingImported ? 'solid' : 'none',
        borderColor: props.id.startsWith(castellBeingImported) ? 'rgba(255, 0, 0, 1)' : _boxStyle.backgroundColor,
    }

    const fontSizeStyle = {
        'fontSize': getFontSize(props.info, boxProps, fontSize),
        ['transform']: parseTransformation(
            fixOrientation(boxProps.transform, textProps.transform)
        ),
    }

    const textStyle = {
        ..._textStyle,
        ...fontSizeStyle,
    }

    const deletedUser = Object.keys(props.castellersInfo).length > 0 && props.assignat !== -1 && !(props.assignat in props.castellersInfo);
    const textValue = deletedUser ? 'ELIMINAT' : props.assignat in props.castellersInfo ? getName(props.castellersInfo[props.assignat], props) : (props.isEscaleta ? '🪜 ' : '')
    const isTemporary = Number(props.castellersInfo[props.assignat]?.is_temporary) === 1;
    const altura = props.assignat in props.castellersInfo ? props.castellersInfo[props.assignat]?.altura : '';
    const altura_mans = props.assignat in props.castellersInfo ? props.castellersInfo[props.assignat]?.altura_mans : '';
    const mostraAlturaMans = hasAlturaMansFeature();
    const isSelected = props.caixaSelected === props.id;
    const copilotGhostCasteller =
        props.assignat === -1 &&
        props.copilotSuggestionCastellerId in props.castellersInfo
            ? props.castellersInfo[props.copilotSuggestionCastellerId]
            : null;

    const isRotated = boxProps.width < boxProps.height;
    const notComingToDiada = (props.assignat > -1 && targetEvent) && !['Vinc', 'Fitxat'].includes(targetAssistencia)
    const targetAssistenciaSimple = targetAssistencia === 'No vinc' ? 'novinc' :
        targetAssistencia === 'No confirmat' ? 'noconfirmat' :
        targetAssistencia === 'Fitxat' ? 'fitxat' :
        targetAssistencia === 'Vinc' ? 'vinc' :
        'noconfirmat'

    const entrada = props.arribenTard.includes(props.assignat) ? applyTimeZone(fullAssistencia?.['data-entrada'])?.format('HH:mm') : null;
    const sortida = props.surtenAviat.includes(props.assignat) ? applyTimeZone(fullAssistencia?.['data-sortida'])?.format('HH:mm') : null;

    return (
        <SelectableCaixa selected={isSelected} {...props}>
            <div id={`caixa-${props.id}`} style={boxStyle} className={`rect ${isRotated ? 'rotated' : ''}`}>
                <div style={{...textStyle, ...{ display: 'flex', flexDirection: 'row', gap: 5 }}}>
                    {
                        props.arribenTard.includes(props.assignat) && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: -12,
                                    left: 0,
                                    fontSize: 6,
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 0, 0, 0.8)',
                                    padding: 1,
                                    borderRadius: 2,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                🛬{entrada}
                            </div>
                        )
                    }
                    {
                        props.surtenAviat.includes(props.assignat) && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: -12,
                                    left: 20, // Adjust left position for the second bubble
                                    fontSize: 6,
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 0, 0, 0.8)',
                                    padding: 1,
                                    borderRadius: 2,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                🛫{sortida}
                            </div>
                        )
                    }

                    {
                        // Desactivat
                        false && props.assignat > -1 && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <ProfilePic
                                    user={props.assignat}
                                    width={20}
                                    height={20}
                                />
                            </div>
                        )
                    }

                    {textValue ? <div
                        className={`text ${notComingToDiada ? `diada-${targetAssistenciaSimple}` : ''} ${readonly && isBrowser ? 'projectormode' : ''}`}
                        style={{
                            fontSize: readonly ? fontSize : getFontSize(props.info, boxProps, fontSize),
                        }}
                    >
                        {textValue}
                        {isTemporary && <span className="temporary-person-box-badge" role="img" aria-label={temporaryPersonLabel} title={temporaryPersonLabel}>
                            <TemporaryPersonClock size="1em" />
                        </span>}
                    </div> : null}
                    {!readonly && <CopilotGhostText casteller={copilotGhostCasteller} />}
                    { (!readonly && altura !== null && altura !== '' && !isNaN(altura)) && <div className={`altura ${setPerfil(altura)}`} style={{ fontSize: getFontSize(props.info, boxProps, fontSize) }}>{altura}</div> }
                </div>
                { (mostraAlturaMans && !readonly && !['tronc', 'baix'].includes(perfil) && !['tronc'].includes(pestanya.toLowerCase()) && altura_mans !== null && altura_mans !== '' && !isNaN(altura_mans)) && <div className={`individual-display relative ${isRotated ? 'rotated' : ''}`} style={{ backgroundColor: '#FAAB78' }}>{altura_mans}</div> }
            </div>
        </SelectableCaixa>
    );
}

export default React.memo(Caixa, (props, newProps)=> {
    const caixaSelectedHasNotChanged = props.caixaSelected === newProps.caixaSelected;

    const infoHasNotChanged = newProps.assignat in newProps.castellersInfo ? JSON.stringify(props.castellersInfo[newProps.assignat]) === JSON.stringify(newProps.castellersInfo[newProps.assignat]) : true;
    const assignatHasNotChanged = props.assignat === newProps.assignat;

    const arribaTardHasNotChanged = props.arribenTard.includes(props.assignat) === newProps.arribenTard.includes(newProps.assignat)
    const surtAviatHasNotChanged = props.surtenAviat.includes(props.assignat) === newProps.surtenAviat.includes(newProps.assignat)

    const plantillaHasNotChanged = Object.keys(props.json).length === Object.keys(newProps.json).length;

    const rotationValHasNotChanged = props.rotationVal === newProps.rotationVal;

    const targetAssistenciaHasNotChanged = props.targetAssistencia === newProps.targetAssistencia;
    const targetEventHasNotChanged = props.targetEvent === newProps.targetEvent;

    const assistenciaHasNotChanged = props.assistencia === newProps.assistencia;

    const opacityHasNotChanged = props.opacity === newProps.opacity;
    const positionHasNotChanged = props.position === newProps.position;
    
    const showUntilHasNotChanged = props.showUntil === newProps.showUntil && props.hasCordons === newProps.hasCordons;
    const isModelHasNotChanged = props.isModels === newProps.isModels;

    const isEscaletaHasNotChanged = props.isEscaleta === newProps.isEscaleta;
    const castellBeingImportedHasNotChanged = props.castellBeingImported === newProps.castellBeingImported;
    const copilotSuggestionHasNotChanged = props.copilotSuggestionCastellerId === newProps.copilotSuggestionCastellerId;

    const fontSizeHasNotChanged = props.fontSize === newProps.fontSize;

    // Do not re-render if...
    return assistenciaHasNotChanged && castellBeingImportedHasNotChanged && isEscaletaHasNotChanged && isModelHasNotChanged && showUntilHasNotChanged && positionHasNotChanged && opacityHasNotChanged && targetEventHasNotChanged && targetAssistenciaHasNotChanged && caixaSelectedHasNotChanged && infoHasNotChanged && assignatHasNotChanged && arribaTardHasNotChanged && surtAviatHasNotChanged && plantillaHasNotChanged && rotationValHasNotChanged && fontSizeHasNotChanged && copilotSuggestionHasNotChanged;
});
